import { useState, useEffect } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useNavigate,
} from "react-router-dom";
import BirthDataForm from "./components/Forms/BirthDataForm";
import HelioChart from "./components/Chart/HelioChart";
import InterpretationsPanel from "./components/Interpretations/InterpretationsPanel";
import AboutPage from "./components/About/AboutPage";
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";
import SubscriptionWarning from "./components/Layout/SubscriptionWarning";
import Landing from "./pages/Landing";
import Login from "./pages/Auth/Login";
import Signup from "./pages/Auth/Signup";
import { ChartProvider } from "./contexts/ChartContext";
import { InterpretationsProvider } from "./contexts/InterpretationsContext";
import { SettingsProvider, useSettings } from "./contexts/SettingsContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "react-hot-toast";
import { useChartOperations } from "./hooks/useChart";
import { useInterpretations } from "./hooks/useInterpretations";
import { useSettingsInitializer } from "./hooks/useSettingsInitializer";
import ProfilePage from "./pages/Profile/Profile";
import "./App.css";
import Plans from "./pages/Plans";
import PaymentSuccess from "./pages/PaymentSuccess";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import { getRotatingIndex } from "./utils/dateUtils";
import ReportGeneration from "./pages/ReportGeneration";
import ErrorBoundary from "./components/ErrorBoundary";
import PrivateRoute from "./components/Layout/PrivateRoute";

const PLANET_CYCLE_LENGTH = 9; // Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto

function AstrologyWorkspace() {
	const { chartResponse, error, isSubmitting, handleGenerateChart } =
		useChartOperations();
	const { setProviders, setActiveProvider, setOrbSettings, setCostSummary } =
		useSettings();
	const { user, refreshUser } = useAuth();
	const {
		interpretations,
		interpretationError,
		loadingTabs,
		loadedTabs,
		generatePlanetaryInterpretations,
		generateAspectInterpretations,
		generateSynthesisInterpretation,
		generateTransitForChart,
		generateSynastryForChart,
		resetInterpretations,
	} = useInterpretations();
	const [showAbout, setShowAbout] = useState(false);
	const [activeTab, setActiveTab] = useState('planets');
	// Initialize settings in background - doesn't block UI
	useSettingsInitializer({
		setProviders,
		setActiveProvider,
		setOrbSettings,
		setCostSummary,
	});

	const handleGenerateChartWithInterpretations = async (payload) => {
		// Reset all previous interpretation data when starting a new chart generation
		resetInterpretations();

		try {
			await handleGenerateChart(payload);
			if (user?.planTier === 'premium') {
				refreshUser();
			}
		} catch (err) {
			// Error already handled in handleGenerateChart
		}
	};

	// Reset interpretations when chart is cleared (e.g., new generation starts or chart is cleared)
	useEffect(() => {
		if (!chartResponse) {
			resetInterpretations();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartResponse]);

	// Auto-fetch interpretations when chart is generated or tab changes
	useEffect(() => {
		if (chartResponse) {
			const isLoaded = loadedTabs?.[activeTab];
			const isLoading = loadingTabs?.[activeTab];

			if (!isLoaded && !isLoading) {
				let cycleIndex = undefined;
				if (user?.planTier !== 'premium') {
					const startDate = user?.createdAt || new Date();
					cycleIndex = getRotatingIndex(startDate, PLANET_CYCLE_LENGTH);
				}

				if (activeTab === 'planets') {
					generatePlanetaryInterpretations(chartResponse, "regular", cycleIndex);
				}
				else if (activeTab === 'dwarf' && user.planTier === 'premium') {
					// Only fetch if dwarf planets are fully calculated
					if (!chartResponse.loading?.dwarf) {
						console.log('Fetching dwarf planets...');
						generatePlanetaryInterpretations(chartResponse, "dwarf", cycleIndex);
					}
				}
				else if (activeTab === 'majorAspects') {
					generateAspectInterpretations(chartResponse, "major");
				}
				else if (activeTab === 'minorAspects' && user.planTier === 'premium') {
					generateAspectInterpretations(chartResponse, "minor");
				}
				else if (activeTab === 'synthesis' && user.planTier === 'premium') {
					generateSynthesisInterpretation(chartResponse);
				}
			}
		}
	}, [chartResponse, activeTab, loadedTabs, loadingTabs, user]);

	useEffect(() => {
		if (user?.planTier === 'premium' && loadedTabs?.planets) {
			refreshUser();
		}
	}, [loadedTabs?.planets, user?.planTier, refreshUser]);

	// UI loads immediately - settings initialize in background with defaults
	// Provider selection removed - using Openonly
	return (

		<div className='app-container'>
			{showAbout && <AboutPage onClose={() => setShowAbout(false)} />}

			<div className='app-header'>
				<h1 className='app-title text-white'>Heliocentric Astrology</h1>
				<button
					className='app-about-button'
					onClick={() => setShowAbout(true)}
					aria-label='Learn about heliocentric astrology'
				>
					Learn More
				</button>
			</div>
			<BirthDataForm
				onSubmit={handleGenerateChartWithInterpretations}
				submitting={isSubmitting}
			/>
			{error && (
				<div className='app-card app-card--error'>
					<h3>Something went wrong</h3>
					<p>{error}</p>
				</div>
			)}
			<div className='app-card'>
				<h3>Heliocentric Chart Preview</h3>
				<HelioChart
					planets={chartResponse?.planets}
					aspects={chartResponse?.aspects}
					orbLegend={chartResponse?.orbLegend}
				/>
			</div>
			<InterpretationsPanel
				interpretations={interpretations}
				loadingTabs={loadingTabs}
				loadedTabs={loadedTabs}
				chartData={chartResponse}
				onGeneratePlanetary={generatePlanetaryInterpretations}
				onGenerateAspects={generateAspectInterpretations}
				onGenerateSynthesis={generateSynthesisInterpretation}
				onGenerateTransit={generateTransitForChart}
				onGenerateSynastry={generateSynastryForChart}
				error={interpretationError}
				hasChart={!!chartResponse}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/>
		</div>
	);
}

const AstrologyExperience = () => (
	<SettingsProvider>
		<AstrologyWorkspace />
	</SettingsProvider>
);

const ProfileExperience = () => (
	<SettingsProvider>
		<ProfilePage />
	</SettingsProvider>
);

function App() {
	return (
		<ErrorBoundary>
			<AuthProvider>
				<Toaster position="top-center" reverseOrder={false} containerStyle={{ zIndex: 99999 }} />
				<Router>
					<ChartProvider>
						<InterpretationsProvider>
							<div className='app-shell'>
								<Header />
								<SubscriptionWarning />
								<main className='app-main'>
									<Routes>
								<Route path='/' element={<Landing />} />
								<Route path='/login' element={<Login />} />
								<Route path='/signup' element={<Signup />} />
							<Route path='/report-generation' element={<ReportGeneration />} />
							<Route path='/report-generation/:orderId' element={<ReportGeneration />} />
							<Route path='/privacy-policy' element={<PrivacyPolicy />} />
							<Route path='/terms-conditions' element={<TermsConditions />} />

							{/* Protected Routes */}
								<Route element={<PrivateRoute />}>
									<Route path='/app' element={<AstrologyExperience />} />
									<Route path='/profile' element={<ProfileExperience />} />
									<Route path='/plans' element={<Plans />} />
									<Route path='/success' element={<PaymentSuccess />} />
									<Route path='/subscription-success' element={<SubscriptionSuccess />} />
								</Route>

								<Route path='*' element={<Navigate to='/' replace />} />
							</Routes>
								</main>
								<Footer />
							</div>
						</InterpretationsProvider>
					</ChartProvider>
				</Router>
			</AuthProvider>
		</ErrorBoundary>
	);
}

export default App;
